import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { extractVariables } from '../../../services/api';

// Custom Dropdown Component (same as PromptWizard)
const TypeDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  const options = ['Single Value', 'Array List'];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  TypeDropdown.propTypes = {
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired
  };

  return (
    <div className="custom-dropdown" ref={dropdownRef}>
      <button
        type="button"
        className={`dropdown-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="dropdown-value">{value}</span>
        <svg className="dropdown-chevron" width="10" height="6" viewBox="0 0 10 6">
          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {isOpen && (
        <div className="dropdown-menu">
          {options.map((option) => (
            <button
              key={option}
              type="button"
              className={`dropdown-option ${value === option ? 'selected' : ''}`}
              onClick={() => {
                onChange(option);
                setIsOpen(false);
              }}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const StepVariables = ({
  formData,
  updateFormData
}) => {
  const [extractedVars, setExtractedVars] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const extract = async () => {
      setIsLoading(true);
      try {
        const result = await extractVariables({
          system_prompt: formData.system_prompt,
          user_prompt: formData.user_prompt,
          few_shot_examples: formData.few_shot_examples
        });
        
        if (result.success && result.data.variables) {
          const vars = result.data.variables.map(varName => {
            const existing = formData.variables.find(v => v.name === varName);
            return existing || {
              name: varName,
              description: '',
              type: 'Single Value',
              required: true
            };
          });
          setExtractedVars(vars);
          updateFormData('variables', vars);
        }
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };

    extract();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateVariable = (index, field, value) => {
    const updated = [...formData.variables];
    updated[index] = { ...updated[index], [field]: value };
    updateFormData('variables', updated);
  };

  const addVariable = () => {
    const updated = [
      ...formData.variables,
      { name: '', description: '', type: 'Single Value', required: true }
    ];
    updateFormData('variables', updated);
  };

  const removeVariable = (index) => {
    const updated = formData.variables.filter((_, i) => i !== index);
    updateFormData('variables', updated);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-sm text-text-secondary">Extracting variables from your prompts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wizard-step">
      <div className="step-intro">
        <h3>Configure Variables ({formData.variables.length})</h3>
        <p>Fill in details for each variable. Leave descriptions blank to auto-generate later.</p>
      </div>

      {formData.variables.length === 0 ? (
        <div className="empty-state-card">
          <p className="text-sm text-text-secondary mb-4">
            No variables detected in your prompts
          </p>
          <button type="button" onClick={addVariable} className="btn-add-var">
            + Add Variable Manually
          </button>
        </div>
      ) : (
        <>
          <div className="variables-list">
            {formData.variables.map((variable, index) => (
              <div key={index} className="variable-card">
                <div className="variable-header">
                  <span className="variable-label">VARIABLE {index + 1}</span>
                  <button
                    type="button"
                    onClick={() => removeVariable(index)}
                    className="btn-remove-var"
                  >
                    Remove
                  </button>
                </div>

                <div className="variable-fields">
                  <div className="form-group">
                    <label className="form-label-sm">Name</label>
                    <input
                      type="text"
                      value={variable.name}
                      onChange={(e) => updateVariable(index, 'name', e.target.value)}
                      placeholder="e.g., customer_name"
                      className="form-input font-mono"
                      readOnly={variable.name && extractedVars.some(ev => ev.name === variable.name)}
                    />
                    <span className="field-hint">Only letters, numbers, and underscore (_)</span>
                  </div>

                  <div className="form-group">
                    <label className="form-label-sm">Description</label>
                    <input
                      type="text"
                      value={variable.description}
                      onChange={(e) => updateVariable(index, 'description', e.target.value)}
                      placeholder="e.g., The customer's full name"
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label-sm">Type</label>
                    <TypeDropdown
                      value={variable.type || 'Single Value'}
                      onChange={(val) => updateVariable(index, 'type', val)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="variable-actions">
            <button type="button" onClick={addVariable} className="btn-add-var">
              + Add Variable
            </button>
          </div>
        </>
      )}
    </div>
  );
};

StepVariables.propTypes = {
  formData: PropTypes.object.isRequired,
  updateFormData: PropTypes.func.isRequired,
};

export default StepVariables;

